import React, { useEffect, useState, useContext } from "react";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaGripLines, FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import {
  deleteWhisperMusic,
  getWhisperMusic,
  postWhisperMusic,
  updateWhisperMusic,
  audioContentSequence,
  swapAudioContentWithPage,
} from "../../../../../libs/api/whisperMusicApi";
import { AppContext } from "../../../../../context/appContext";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../../libs/LoadingSpinner";
import Pagination from "../../../../../libs/Pagination";

export default function WhisperMusic({ currentCategoryId = null }) {
  const { audioCategoriesDropdown, fetchAudioCategoriesDropdown, t, formatNum, isRTL } = useContext(AppContext);
  const [isMusicListLoading, setIsMusicListLoading] = useState(false);
  const [musicListDataArr, setMusicListDataArr] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [modalMode, setModalMode] = useState("add");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMusicId, setSelectedMusicId] = useState(null);
  const [category, setCategory] = useState("");
  const [form, setForm] = useState({ title: "", cover: null, audio: null });
  const [isHidden, setIsHidden] = useState(false);
  const [isAddMusicLoading, setIsAddMusicLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [errors, setErrors] = useState({ title: "", cover: "", audio: "" });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalPages: 0,
    totalRecords: 0,
  });

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Modal functions
  const openModal = (mode, idx = null) => {
    // Refresh audio categories dropdown to get latest categories
    fetchAudioCategoriesDropdown();

    setModalMode(mode);
    setSelectedIdx(idx);
    if (mode === "edit" && idx !== null) {
      // Putting Data in Form
      setSelectedMusicId(musicListDataArr[idx]._id);
      setCategory(musicListDataArr[idx].categoryId?._id || "");
      setForm({
        title: musicListDataArr[idx].title,
        cover: musicListDataArr[idx].coverFile || musicListDataArr[idx].cover,
        audio: musicListDataArr[idx].audio || musicListDataArr[idx].audio,
      });
      setIsHidden(musicListDataArr[idx].isHidden || false);
    } else {
      setCategory("");
      setForm({ title: "", cover: null, audio: null });
      setIsHidden(false);
    }
    setErrors({ title: "", cover: "", audio: "" });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm({ title: "", cover: null, audio: null });
    setIsHidden(false);
    setSelectedIdx(null);
    setErrors({ title: "", cover: "", audio: "" });
    setSelectedMusicId(null);
  };
  // Modal functions end

  // Handle remove current audio file
  const handleRemoveCurrentAudioFile = (e) => {
    e.stopPropagation();
    setForm({ ...form, audio: null });
  };
  // Handle remove current audio file end

  // Validation functions
  const validate = () => {
    const newErrors = {};
    if (!form.title.trim()) newErrors.title = t('audio.titleRequired');
    if (!form.cover) newErrors.cover = t('audio.coverImageRequired');
    if (!form.audio) newErrors.audio = t('audio.mp3FileRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Validation functions end

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setForm({ ...form, [name]: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // Handle drop
  const handleDrop = (e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setForm({ ...form, [type]: file });
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination({ ...pagination, currentPage: newPage });
    }
  };

  // api calls functions start
  const getWhisperMusicFunc = async (search, pagination) => {
    setIsMusicListLoading(true);
    try {
      const categoryIdFilter = currentCategoryId === null ? 'null' : currentCategoryId;
      const response = await getWhisperMusic(search, { ...pagination, categoryId: categoryIdFilter });
      if (response?.data?.success) {
        const musicWithSequence = response?.data?.data.map((music, index) => ({
          ...music,
          sequence: music.sequence || index + 1
        }));
        setMusicListDataArr(musicWithSequence);
        setPagination({
          ...pagination,
          totalPages: response?.data?.pagination?.totalPages,
          totalRecords: response?.data?.pagination?.totalRecords,
        });
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsMusicListLoading(false);
    }
  };

  const postWhisperMusicFunc = async (data) => {
    setIsAddMusicLoading(true);
    try {
      const response = await postWhisperMusic(data);
      if (response?.data?.success) {
        toast.success(response?.data?.message || t('audio.musicAddedSuccess'));
        await getWhisperMusicFunc(debouncedSearch, pagination);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsAddMusicLoading(false);
    }
  };

  const updateWhisperMusicFunc = async (id, data) => {
    setIsAddMusicLoading(true);
    try {
      const response = await updateWhisperMusic(id, data);
      if (response?.status === 200) {
        toast.success(response?.data?.message || t('audio.musicUpdatedSuccess'));
        await getWhisperMusicFunc(debouncedSearch, pagination);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setIsAddMusicLoading(false);
    }
  };

  const deleteWhisperMusicFunc = async (id) => {
    try {
      const response = await deleteWhisperMusic(id);
      if (response?.status === 200 || response?.status === 201) {
        toast.success(response?.data?.message || t('audio.musicDeletedSuccess'));
        await getWhisperMusicFunc(debouncedSearch, pagination);
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const formData = new FormData();
    formData.append("title", form.title);
    // Always append categoryId, even if empty (to allow clearing category)
    formData.append("categoryId", category || "");
    if (form.cover) {
      formData.append("cover", form.cover); // form.cover should be a File object
    }
    if (form.audio) {
      formData.append("audio", form.audio); // form.audio should be a File object
    }
    formData.append("isHidden", isHidden ? "true" : "false");

    if (modalMode === "add") {
      // setMusicListDataArr([...musicListDataArr, newMusic]);
      await postWhisperMusicFunc(formData);
    } else if (modalMode === "edit" && selectedIdx !== null) {
      await updateWhisperMusicFunc(selectedMusicId, formData);
    }
    closeModal();
  };

  const handleDelete = async (id) => {
    await deleteWhisperMusicFunc(id);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(musicListDataArr[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);

    const draggedElement = e.target;
    const rect = draggedElement.getBoundingClientRect();
    const dragImage = draggedElement.cloneNode(true);

    dragImage.style.width = rect.width + 'px';
    dragImage.style.height = rect.height + 'px';
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    dragImage.style.backgroundColor = 'white';
    dragImage.style.border = '2px solid #0D6EFD';
    dragImage.style.borderRadius = '8px';
    dragImage.style.zIndex = '9999';

    draggedElement.style.opacity = '0.3';

    e.dataTransfer.setDragImage(dragImage, rect.width / 2, rect.height / 2);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(musicListDataArr[index]);
  };

  const handleDropItem = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = musicListDataArr[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence;
    const targetSequence = draggedOverItem.sequence;

    const updatedMusicList = musicListDataArr.map(music => {
      if (music._id === draggedItem._id) {
        return { ...music, sequence: targetSequence };
      }
      if (music._id === draggedOverItem._id) {
        return { ...music, sequence: draggedSequence };
      }
      return music;
    });

    // Sort by sequence to reflect the swap
    updatedMusicList.sort((a, b) => a.sequence - b.sequence);

    setMusicListDataArr(updatedMusicList);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);

    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.failedToUpdateSequence'));
      getWhisperMusicFunc(debouncedSearch, pagination);
    }
  };

  const handleDragEnd = (e) => {
    if (e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const payload = {
        id: id,
        sequence: sequence
      }
      const response = await audioContentSequence(payload);
      if (response.status === 200 || response.status === 201) {
        // Silently update sequence
      }
    } catch (error) {
      toast.error(error?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const handleSwapWithPage = async (musicId, direction) => {
    try {
      const payload = {
        id: musicId,
        direction: direction,
        currentPage: pagination.currentPage,
        limit: pagination.limit
      };
      const response = await swapAudioContentWithPage(payload);
      if (response.status === 200) {
        toast.success(response.data.message || t('messages.audioMovedSuccessfully'));
        getWhisperMusicFunc(search, pagination);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getWhisperMusicFunc(debouncedSearch, pagination);
  }, [pagination.currentPage, debouncedSearch, currentCategoryId]);

  return (
    <div className="p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <h2 className="text-2xl font-semibold mb-4">{t('audio.whisperMusic')}</h2>
      <div className="bg-white rounded-3xl shadow">
        {/* Search and Add Music */}
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('forms.searchPlaceholder')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              dir={isRTL ? 'rtl' : 'ltr'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2 border-0 rounded-xl hover:bg-blue-700"
          >
            <IoMdAdd size={22} /> {t('audio.addMusic')}
          </button>
        </div>

        {/* Table */}
        {musicListDataArr.length === 0 && !isMusicListLoading ? (
          <div className="flex justify-center items-center h-full p-6">
            <p className="text-gray-500">{t('audio.noMusic')}</p>
          </div>
        ) : isMusicListLoading ? (
          <div className="flex justify-center items-center h-full p-6">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="w-full">
            {/* Table Header */}
            <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-4 border-b border-[#F7F7F9]">
              <div className={`p-2 py-4 ${isRTL ? 'pr-8' : 'pl-8'}`}>{t('forms.title')}</div>
              <div className="p-2 py-4">{t('audio.cover')}</div>
              <div className="p-2 py-4">{t('audio.mp3')}</div>
              <div className="p-2 py-4">{t('common.actions')}</div>
            </div>
            <div>
              {musicListDataArr.map((music, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDropItem(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={`grid grid-cols-4 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] select-none ${
                    draggedItem?._id === music._id ? "opacity-50 bg-gray-100 shadow-lg" : ""
                  } ${
                    dragOverItem?._id === music._id ? "border-t-2 border-blue-500 bg-blue-50" : ""
                  }`}
                  style={{
                    cursor: 'move',
                    transition: 'all 0.3s ease',
                    userSelect: 'none'
                  }}
                >
                  <div className={`p-2 py-6 ${isRTL ? 'pr-8' : 'pl-8'} flex items-center gap-2`}>
                    <FaGripLines className="text-gray-400" />
                    {music.title}
                    {music?.isHidden && (
                      <span className="badge badge-sm bg-gray-200 text-gray-600 border-0">{t('forms.hidden')}</span>
                    )}
                  </div>
                  <div className="p-2 py-6 flex items-center">
                    {music.cover && (
                      <img
                        src={music.cover}
                        alt="cover"
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                  </div>
                  <div className="p-2 py-6 flex items-center">
                    {music.audio && (
                      <audio
                        controls
                        src={music.audio}
                        className="w-48 h-10 min-w-[120px] max-w-full"
                      />
                    )}
                  </div>
                  <div className="p-2  flex items-center">
                    <div className="flex gap-3 items-center">
                      {idx === 0 && pagination.currentPage > 1 && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(music._id, 'previous');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToPreviousPage')}
                        >
                          <FaArrowLeft size={18} />
                        </button>
                      )}
                      {idx === pagination.limit - 1 && pagination.currentPage < pagination.totalPages && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(music._id, 'next');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToNextPage')}
                        >
                          <FaArrowRight size={18} />
                        </button>
                      )}
                      <BiEditAlt
                        size={24}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => openModal("edit", idx)}
                      />
                      <FaRegTrashCan
                        size={20}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => handleDelete(music._id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!isMusicListLoading && musicListDataArr.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((pagination.currentPage - 1) * pagination.limit + 1)} {t('pagination.to')}{" "}
              {formatNum(Math.min(
                pagination.currentPage * pagination.limit,
                pagination.totalRecords
              ))}{" "}
              {t('pagination.of')} {formatNum(pagination.totalRecords)} {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl min-w-[400px] max-w-[95vw] w-full md:w-[500px] p-0 shadow-lg" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-lg">
                  {modalMode === "add" ? t('audio.addMusic') : t('audio.editMusic')}
                </h3>
                <button
                  className="text-xl hover:text-red-500"
                  onClick={closeModal}
                >
                  <IoClose />
                </button>
              </div>

              {/* Category Dropdown */}
              <label className="block text-sm font-medium mb-2">
                {t('audio.categoryOptional')}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                <option value="">{t('audio.selectCategoryOptional')}</option>
                {audioCategoriesDropdown?.data?.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.displayTitle || cat.title}
                  </option>
                ))}
              </select>

              {/* Cover Image Dropzone */}
              <label className="block text-sm font-medium mb-2">
                {t('audio.coverImage')}
              </label>
              <div
                className="relative w-full border-dashed border-2 border-gray-300 p-6 text-center rounded-xl cursor-pointer hover:bg-gray-50 mb-4"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, "cover")}
                onClick={() =>
                  document.getElementById("coverUploadInput").click()
                }
              >
                {form.cover ? (
                  <img
                    src={
                      typeof form.cover === "string"
                        ? form.cover
                        : URL.createObjectURL(form.cover)
                    }
                    alt="cover"
                    className="max-h-32 mx-auto mb-2"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
                    <span className="text-md text-blue-500 font-medium">
                      {t('audio.clickToUploadCover')}
                    </span>
                    <span className="text-md text-black">{t('forms.orDragDrop')}</span>
                    <span className="text-sm text-black">
                      {t('audio.maxFileSize10MB')}
                    </span>
                  </div>
                )}
                <input
                  id="coverUploadInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) =>
                    handleInputChange({
                      target: { name: "cover", files: e.target.files },
                    })
                  }
                />
              </div>
              {errors.cover && (
                <p className="text-red-500 text-xs mb-2">{errors.cover}</p>
              )}
              {/* Title */}
              <label className="block text-sm font-medium mb-2">{t('forms.title')}</label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleInputChange}
                placeholder={t('audio.enterAudioTitle')}
                className="w-full mb-4 border rounded-xl p-3 border-[#808080]"
                dir={isRTL ? 'rtl' : 'ltr'}
              />
              {errors.title && (
                <p className="text-red-500 text-xs mb-2">{errors.title}</p>
              )}
              {/* MP3 Upload */}
              <label className="block text-sm font-medium mb-2">{t('audio.mp3File')}</label>
              <div
                className="relative w-full border-dashed border-2 border-gray-300 p-6 text-center rounded-xl cursor-pointer hover:bg-gray-50 mb-4"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, "audio")}
                onClick={(e) =>
                  !form.audio &&
                  document.getElementById("mp3UploadInput").click()
                }
              >
                {form.audio ? (
                  <div className="flex gap-2 items-center justify-between">
                    <audio
                      controls
                      src={
                        typeof form.audio === "string"
                          ? form.audio
                          : URL.createObjectURL(form.audio)
                      }
                      className="w-full"
                    />
                    <button
                      className="text-red-500 hover:text-red-700"
                      onClick={handleRemoveCurrentAudioFile}
                    >
                      <IoClose size={24} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
                      <span className="text-md text-blue-500 font-medium">
                        {t('audio.clickToUploadMp3')}
                      </span>
                      <span className="text-md text-black">
                        {t('forms.orDragDrop')}
                      </span>
                      <span className="text-sm text-black">
                        {t('audio.mp3OnlyMaxSize')}
                      </span>
                    </div>

                    <input
                      id="mp3UploadInput"
                      type="file"
                      accept="audio/mp3,audio/mpeg"
                      className="hidden"
                      onChange={(e) =>
                        handleInputChange({
                          target: { name: "audio", files: e.target.files },
                        })
                      }
                    />
                  </>
                )}
              </div>
              {errors.audio && (
                <p className="text-red-500 text-xs mb-2">{errors.audio}</p>
              )}
              {/* Hidden */}
              <div className="flex items-center gap-2 mt-8">
                <input
                  type="checkbox"
                  id="audioContent_isHidden"
                  checked={isHidden}
                  onChange={(e) => setIsHidden(e.target.checked)}
                  className="checkbox checkbox-sm"
                />
                <label htmlFor="audioContent_isHidden" className="text-sm font-medium cursor-pointer">{t('forms.hidden')}</label>
              </div>
            </div>
            <div className="modal-action rounded-b-2xl bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
              <button
                type="button"
                disabled={isAddMusicLoading}
                onClick={handleSubmit}
                className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
              >
                {isAddMusicLoading ? (
                  <LoadingSpinner />
                ) : modalMode === "add" ? (
                  t('common.add')
                ) : (
                  t('common.update')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
